import { useState } from 'react';
import { useAppDispatch } from '@/redux/Store';
import { sendMessage } from '@/redux/thunks/messagesThunks';
import api from '@/utils/api';
import { buildSignBroadcast } from '@/utils/kleverTx';

interface PrepareParams {
  recipientId: string;
}

interface PrepareResponse {
  payload: any;
  metadata: string;
  kdaFee?: string;
  network?: string;
  pricing: {
    spotlightFee: string;
    estimatedGas: string;
    totalTransaction: string;
  };
  recipientProfileId: number;
  canProceed?: boolean;
  warning?: string;
  balance?: {
    current: number;
    required: string;
    shortfall: string | null;
  };
}

interface SendParams {
  threadId: string;
  content: string;
}

export const useDMSpotlight = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [spotlightFee, setSpotlightFee] = useState<string | null>(null);
  const [preparedData, setPreparedData] = useState<PrepareResponse | null>(null);

  /**
   * Step 1: Prepare transaction (fetch fee and build transaction)
   */
  const prepareTransaction = async (params: PrepareParams): Promise<{ price: number; canProceed?: boolean; warning?: string; balance?: any }> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<PrepareResponse>('/messages/spotlight/prepare', {
        recipientId: params.recipientId,
      });

      const data = response.data;
      setPreparedData(data);
      setSpotlightFee(data.pricing.spotlightFee);

      return {
        price: parseFloat(data.pricing.totalTransaction) || 0,
        canProceed: data.canProceed,
        warning: data.warning,
        balance: data.balance,
      };
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to prepare transaction';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 2: Execute transaction (sign with wallet)
   */
  const executeTransaction = async (): Promise<string> => {
    if (!preparedData) {
      throw new Error('Transaction not prepared');
    }

    setIsLoading(true);
    setError(null);

    try {
      const txHash = await buildSignBroadcast({
        payload: preparedData.payload,
        metadata: preparedData.metadata,
        network: preparedData.network,
        kdaFee: preparedData.kdaFee,
      });
      return txHash;
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to sign transaction';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 3: Send message with txHash (wallet users) or without (email users)
   */
  const sendSpotlightMessage = async (params: SendParams, txHash?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await dispatch(
        sendMessage({
          threadId: params.threadId,
          content: params.content,
          spotlight: {
            enabled: true,
            txHash,
          },
        })
      ).unwrap();
    } catch (err: any) {
      const errorMsg = err || 'Failed to send spotlight message';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setIsLoading(false);
    setError(null);
    setSpotlightFee(null);
    setPreparedData(null);
  };

  return {
    isLoading,
    error,
    spotlightFee,
    prepareTransaction,
    executeTransaction,
    sendSpotlightMessage,
    reset,
  };
};

export default useDMSpotlight;
